import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { getPortfolio } from '../api/trading'
import { getFleetPortfolio } from '../api/fleet'
import { useAuthStore } from '../auth/useAuthStore'
import { portfolioPollMs } from '../lib/marketHours'
import { usePrefetchTicker } from '../hooks/usePrefetchTicker'
import { useMaskMoney } from '../privacy/usePrivacyStore'
import { usePortfolioHoverStore } from './usePortfolioHoverStore'
import type { Position } from '../api/types'

const OCC_RE = /^([A-Z]+)\d{6}[CP]\d{8}$/
const MIN_SLICE_PCT = 0.6

function underlyingOf(symbol: string): string {
  const m = symbol.match(OCC_RE)
  return m ? m[1] : symbol
}

function fmtMoneyRaw(n: number): string {
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function fmtPct(n: number, withSign = false): string {
  if (!Number.isFinite(n)) return '—'
  const sign = withSign && n > 0 ? '+' : ''
  return `${sign}${n.toFixed(2)}%`
}

interface Slice {
  key: string
  kind: 'position' | 'cash'
  symbol: string
  name?: string
  navSymbol?: string
  signedValue: number
  absValue: number
  widthPct: number
  pctOfPortfolio: number
  gainPct?: number
  colorClass: string
}

function colorForGain(gainPct: number | undefined): string {
  if (gainPct === undefined || !Number.isFinite(gainPct)) return 'bg-zinc-500'
  if (gainPct > 5) return 'bg-emerald-500'
  if (gainPct > 1) return 'bg-emerald-600'
  if (gainPct >= 0) return 'bg-emerald-700'
  if (gainPct > -1) return 'bg-red-700'
  if (gainPct > -5) return 'bg-red-600'
  return 'bg-red-500'
}

function positionToSlice(p: Position): Omit<Slice, 'widthPct' | 'pctOfPortfolio'> {
  const signed = Number(p.currentValue)
  const abs = Math.abs(Number.isFinite(signed) ? signed : 0)
  const gainPct = Number(p.costBasis.gainPercentage)
  return {
    key: p.instrument.symbol,
    kind: 'position',
    symbol: p.instrument.symbol,
    name: p.instrument.name,
    navSymbol:
      p.instrument.type === 'OPTION' ? underlyingOf(p.instrument.symbol) : p.instrument.symbol,
    signedValue: Number.isFinite(signed) ? signed : 0,
    absValue: abs,
    gainPct: Number.isFinite(gainPct) ? gainPct : undefined,
    colorClass: colorForGain(Number.isFinite(gainPct) ? gainPct : undefined),
  }
}

function Stat({ label, value, sub, accent }: { label: string; value: string; sub: string; accent?: 'emerald' | 'rose' }) {
  const cls = accent === 'emerald' ? 'text-emerald-400'
            : accent === 'rose'    ? 'text-rose-400'
            : 'text-zinc-50'
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-xl font-semibold font-mono mt-0.5 ${cls}`}>{value}</div>
      <div className="text-[11px] text-zinc-500 font-mono mt-0.5">{sub}</div>
    </div>
  )
}

export function PortfolioBar() {
  const accountId = useAuthStore((s) => s.accountId)
  const scopeAccountId = useAuthStore((s) => s.scopeAccountId)
  const accounts = useAuthStore((s) => s.accounts)
  const isAggregate = scopeAccountId === null && accounts.length > 1
  const navigate = useNavigate()
  const prefetchTicker = usePrefetchTicker()
  const mask = useMaskMoney()
  const fmtMoney = (n: number) => mask(fmtMoneyRaw(n))
  const hovered = usePortfolioHoverStore((s) => s.hovered)
  const setHovered = usePortfolioHoverStore((s) => s.setHovered)

  const fleetQuery = useQuery({
    queryKey: ['fleet', 'portfolio'],
    queryFn: getFleetPortfolio,
    enabled: isAggregate,
    refetchInterval: portfolioPollMs,
  })

  const { data, isLoading, error } = useQuery({
    queryKey: ['portfolio', accountId],
    queryFn: getPortfolio,
    refetchInterval: portfolioPollMs,
    enabled: !!accountId && !isAggregate,
  })

  const { slices, netLiquidation } = useMemo(() => {
    if (!data) return { slices: [] as Slice[], netLiquidation: 0 }

    const positionRaw = (data.positions ?? []).map(positionToSlice)
    const cashEntry = (data.equity ?? []).find((e) => e.type === 'CASH')
    const cashValue = cashEntry ? Number(cashEntry.value) : 0
    const cashAbs = Math.abs(Number.isFinite(cashValue) ? cashValue : 0)

    const raws: Array<Omit<Slice, 'widthPct' | 'pctOfPortfolio'>> = [...positionRaw]
    if (cashAbs > 0) {
      raws.push({
        key: 'cash',
        kind: 'cash',
        symbol: 'Cash',
        signedValue: cashValue,
        absValue: cashAbs,
        colorClass: 'bg-zinc-500',
      })
    }

    const totalAbs = raws.reduce((acc, s) => acc + s.absValue, 0)
    const net = positionRaw.reduce((acc, s) => acc + s.signedValue, 0) + (Number.isFinite(cashValue) ? cashValue : 0)

    if (totalAbs <= 0) return { slices: [] as Slice[], netLiquidation: net }

    raws.sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'cash' ? 1 : -1
      return b.absValue - a.absValue
    })

    const rawPcts = raws.map((s) => (s.absValue / totalAbs) * 100)
    const clamped = rawPcts.map((p) => Math.max(p, MIN_SLICE_PCT))
    const clampedSum = clamped.reduce((a, b) => a + b, 0)
    const widths = clamped.map((p) => (p / clampedSum) * 100)

    const built: Slice[] = raws.map((s, i) => ({
      ...s,
      pctOfPortfolio: rawPcts[i],
      widthPct: widths[i],
    }))

    return { slices: built, netLiquidation: net }
  }, [data])

  if (isAggregate) {
    const p = fleetQuery.data
    if (!p) {
      return (
        <div className="px-4 py-4 border-b border-zinc-800">
          <div className="h-6 w-full rounded bg-zinc-800 animate-pulse" />
        </div>
      )
    }
    return (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-x-6 gap-y-4 p-5 border-b border-zinc-800">
        <Stat label="Total Equity" value={fmtMoney(p.totalEquity)} sub={`${p.totalCount} accounts`} />
        <Stat label="Total Buying Power" value={fmtMoney(p.totalBuyingPower)} sub={`across ${p.totalCount}`} />
        <Stat label="Armed BP" value={fmtMoney(p.armedBuyingPower)} sub={`${p.armedCount} armed`} accent="emerald" />
        <Stat label="Day P/L" value={fmtMoney(p.dayPL)} sub="unrealized" accent={p.dayPL >= 0 ? 'emerald' : 'rose'} />
        {/* TODO: positions count from Task 12 */}
        <Stat label="Open Positions" value="—" sub="—" />
      </div>
    )
  }

  if (!accountId) return null

  return (
    <div className="px-4 py-4 border-b border-zinc-800">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-xs uppercase tracking-wide text-zinc-500">Portfolio</div>
        <div className="font-mono text-zinc-100">
          {isLoading ? '…' : fmtMoney(netLiquidation)}
        </div>
      </div>

      {error && (
        <div className="text-xs text-red-400">Failed to load portfolio</div>
      )}

      {!isLoading && !error && slices.length === 0 && (
        <div className="h-6 w-full rounded bg-zinc-800" />
      )}

      {slices.length > 0 && (
        <div className="relative">
          <div
            className="flex h-6 w-full overflow-hidden rounded bg-zinc-800"
            onMouseLeave={() => setHovered(null)}
          >
            {slices.map((s) => {
              const isHovered = hovered === s.key
              const clickable = s.kind === 'position' && !!s.navSymbol
              return (
                <button
                  key={s.key}
                  type="button"
                  onMouseEnter={() => {
                    setHovered(s.key)
                    if (s.kind === 'position' && s.navSymbol) prefetchTicker(s.navSymbol)
                  }}
                  onFocus={() => setHovered(s.key)}
                  onBlur={() => setHovered(null)}
                  onClick={() => {
                    if (clickable) navigate(`/instruments/${s.navSymbol}`)
                  }}
                  aria-label={`${s.symbol} ${s.pctOfPortfolio.toFixed(2)}%`}
                  style={{ width: `${s.widthPct}%` }}
                  className={`h-full flex items-center justify-center overflow-hidden text-[10px] font-medium leading-none text-white/90 transition-opacity ${s.colorClass} ${
                    clickable ? 'cursor-pointer' : 'cursor-default'
                  } ${
                    hovered && !isHovered ? 'opacity-50' : 'opacity-100'
                  } border-r border-zinc-900 last:border-r-0`}
                >
                  {s.widthPct >= 4 && (
                    <span className="truncate px-1">
                      {isHovered
                        ? (s.navSymbol ?? s.symbol)
                        : s.pctOfPortfolio < 1
                          ? `${s.pctOfPortfolio.toFixed(1)}%`
                          : `${Math.round(s.pctOfPortfolio)}%`}
                    </span>
                  )}
                </button>
              )
            })}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs min-h-[1.25rem]">
            {(() => {
              const s = hovered ? slices.find((x) => x.key === hovered) : null
              if (s) {
                return (
                  <>
                    <span className="font-mono text-zinc-100">{s.symbol}</span>
                    {s.name && <span className="text-zinc-500">{s.name}</span>}
                    <span className="text-zinc-300">{fmtMoney(s.signedValue)}</span>
                    <span className="text-zinc-400">{fmtPct(s.pctOfPortfolio)}</span>
                    {s.gainPct !== undefined && (
                      <span className={s.gainPct >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                        {fmtPct(s.gainPct, true)}
                      </span>
                    )}
                  </>
                )
              }
              const positionCount = slices.filter((x) => x.kind === 'position').length
              const cashSlice = slices.find((x) => x.kind === 'cash')
              return (
                <>
                  <span className="text-zinc-400">
                    {positionCount} position{positionCount === 1 ? '' : 's'}
                  </span>
                  {cashSlice && (
                    <span className="text-zinc-500">
                      Cash {fmtMoney(cashSlice.signedValue)} · {fmtPct(cashSlice.pctOfPortfolio)}
                    </span>
                  )}
                  <span className="text-zinc-600">Hover a slice for details</span>
                </>
              )
            })()}
          </div>
        </div>
      )}
    </div>
  )
}
